'use client';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';
import { AnimateInOut, AnimateUpDown } from '../Animate';
import Icon from '../Icon';
import s from './MobileMenu.module.scss';
import Modal from '../Modal/Modal';
import ModalFileSubmissionForm from '../ModalFileSubmissionForm/ModalFileSubmissionForm';
import LatestUpdatesModal from '../LatestUpdatesModal/LatestUpdatesModal';
import SupportFormModal from '../SupportFormModal/SupportFormModal';
import Richtext from '../Richtext/Richtext';
import { t } from '../../i18n';
import Bookmark from '../Bookmark/Bookmark';
import Popover from '../Popover/Popover';
import AccountMenu from '../Toolbar/AccountMenu';
import { useBookmarkContext } from '../../utils/bookmark-provider';

const MobileMenu = ({
    items = { children: [] },
    supportForm,
    logoutUrl,
    user,
    disclaimer = { modal: {} },
    notificationCounts = {},
    secondaryLinks = [],
    latestUpdates = null,
    clientDashboards = [],
    dashboardId = null,
}) => {
    const [isLatestUpdatesOpen, setIsLatestUpdatesOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [manuallyActiveId, setManuallyActiveId] = useState(null);

    const menuRef = useRef(null);

    const toggleMenu = () => {
        setIsExpanded((prev) => {
            const next = !prev;
            if (!next) {
                setManuallyActiveId(null);
            }
            return next;
        });
    };

    const classes = classNames(s.Root, { [s[`Root--Expanded`]]: isExpanded });

    const reportsCount = notificationCounts?.reportPages;

    const lvl1 = [
        { ...items, children: {} },
        ...Object.values(items.children ? items.children : {}),
    ];

    const firstPage = lvl1[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const parseDisclaimerText = (text) => {
        if (!text) {
            return text;
        }

        let regex = /(.*?){{(.*?)}}(.*)/;
        let matches = text.match(regex);

        if (matches) {
            return matches.slice(1, 4);
        } else {
            return text;
        }
    };

    const parsedDisclaimerText = parseDisclaimerText(disclaimer.text);

    return (
        <div className={classes} ref={menuRef}>
            <div className={s.Overlay} />
            <div className={s.Container}>
                <div className={s.Logo}>
                    {!!firstPage?.url && (
                        <Link href={firstPage.url} aria-label="To start page">
                            <Icon name="LogoWhite" />
                        </Link>
                    )}
                </div>
                <div className={s.Menu}>
                    <button className={s.ToggleButton} onClick={toggleMenu}>
                        <span className={s.ToggleButtonText}>
                            {isExpanded ? 'Close' : 'Menu'}
                        </span>
                        <Icon name="MoreVertical" />
                    </button>
                    <AnimateUpDown isVisible={isExpanded}>
                        <div className={s.MenuContent}>
                            <Header
                                user={user}
                                clientDashboards={clientDashboards}
                            />
                            <div className={s.NavWrapper}>
                                <nav className={s.Nav}>
                                    <List
                                        items={lvl1}
                                        navId="nav"
                                        reportsCount={reportsCount}
                                        manuallyActiveId={manuallyActiveId}
                                        setManuallyActiveId={
                                            setManuallyActiveId
                                        }
                                    />
                                </nav>
                                {(latestUpdates || !!secondaryLinks.length) && (
                                    <div className={s.SecondaryLinks}>
                                        {latestUpdates && (
                                            <button
                                                className={s.SecondaryLink}
                                                onClick={() =>
                                                    setIsLatestUpdatesOpen(true)
                                                }>
                                                {t('latestUpdates.menuLabel')}
                                            </button>
                                        )}
                                        {secondaryLinks.map((link, index) => (
                                            <a
                                                key={index}
                                                href={link.url}
                                                className={s.SecondaryLink}>
                                                {link.title}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={s.Footer}>
                                <div className={s.FooterButtons}>
                                    {dashboardId && (
                                        <ModalFileSubmissionForm
                                            dashboardId={dashboardId}
                                            isCustomButton
                                            buttonContent={
                                                <div className={s.FooterButton}>
                                                    <Icon name="FolderUpload" />
                                                    {t(
                                                        'modalFileSubmissionForm.submitNewRequest'
                                                    )}
                                                </div>
                                            }
                                        />
                                    )}
                                    <a
                                        href={logoutUrl}
                                        className={s.FooterButton}
                                        type="button"
                                        aria-label="Logout">
                                        <Icon name="Logout" />
                                        Log out
                                    </a>
                                </div>
                                <Modal
                                    title={disclaimer.modal.title}
                                    modifiers={['Sidebar']}
                                    buttonContent={
                                        <div className={s.DisclaimerButton}>
                                            {!Array.isArray(
                                                parsedDisclaimerText
                                            ) ? (
                                                <span
                                                    className={
                                                        s.DisclaimerButtonContent
                                                    }>
                                                    {parsedDisclaimerText}
                                                </span>
                                            ) : (
                                                <div
                                                    className={
                                                        s.DisclaimerButtonContent
                                                    }>
                                                    <span
                                                        className={
                                                            s.DisclaimerButtonText
                                                        }>
                                                        {
                                                            parsedDisclaimerText[0]
                                                        }
                                                        <span
                                                            className={
                                                                s.DisclaimerButtonLink
                                                            }>
                                                            {
                                                                parsedDisclaimerText[1]
                                                            }
                                                        </span>
                                                        {
                                                            parsedDisclaimerText[2]
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    bottomButtonContent={
                                        <div
                                            className={s.DisclaimerModalButton}>
                                            {disclaimer.modal.buttonText}
                                            <span className="sr-only">
                                                , close modal
                                            </span>
                                        </div>
                                    }>
                                    <div className={s.Disclaimer}>
                                        <Richtext
                                            text={disclaimer.modal.content}
                                        />
                                    </div>
                                </Modal>
                            </div>
                        </div>
                    </AnimateUpDown>
                </div>
            </div>
            {latestUpdates && (
                <LatestUpdatesModal
                    {...latestUpdates}
                    isOpen={isLatestUpdatesOpen}
                    onClose={() => setIsLatestUpdatesOpen(false)}
                />
            )}
        </div>
    );
};

const Header = ({ user, clientDashboards }) => {
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const popoverRef = useRef(null);

    useEffect(() => {
        // Closed by a click anywhere else in the menu, not only outside it: the
        // menu stays open behind the popover, and a reader turning to the nav
        // has turned away from the account list.
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                setIsAccountMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={s.Header}>
            <ul className={s.HeaderList}>
                <li className={s.HeaderListItem}>
                    {/* The toolbar's account popover, which the toolbar itself
                        only shows from ML: this is where a reader on a phone
                        reaches the same email and the same client accounts. */}
                    <Popover
                        popoverRef={popoverRef}
                        isPopoverOpen={isAccountMenuOpen}
                        onClick={() => setIsAccountMenuOpen((open) => !open)}
                        // The header row's width, as the design has it —
                        // the row is what the popover measures itself
                        // against, not the name it drops under and not the
                        // viewport, which is wider than the menu.
                        modifiers={['Full']}
                        overrideButton={
                            <button
                                className={s.Button}
                                aria-expanded={isAccountMenuOpen}>
                                <Icon name="AccountCircle" />
                                <span>{user?.name}</span>
                                <Icon
                                    name="SelectChevronWhite"
                                    className={s.ButtonChevron}
                                />
                            </button>
                        }
                        content={
                            <AccountMenu
                                user={user}
                                dashboards={clientDashboards}
                            />
                        }
                    />
                </li>
            </ul>
        </div>
    );
};
const List = ({
    items = [],
    navId = 'nav',
    id = null,
    reportsCount,
    manuallyActiveId,
    setManuallyActiveId,
}) => {
    const { bookmarkedPages } = useBookmarkContext();
    const listId = id === null ? `${navId}-list` : `${navId}-${id}-list`;

    const findLastActive = (items) => {
        let lastActive = null;
        Object.values(items).forEach((item) => {
            if (item.isActive) lastActive = item.id;
        });
        return lastActive;
    };

    const lastActiveFromActive = findLastActive(items);
    const lastActiveIdToUse = manuallyActiveId || lastActiveFromActive;

    const bookmarkItem = {
        id: 'bookmark-tab',
        title: 'Favorites',
        icon: 'BookmarkWhite',
        isActive: manuallyActiveId === 'bookmark-tab',
        children: {},
    };

    const allItems = [...items, bookmarkItem];

    return (
        <div className={s.ListWrapper}>
            <ul className={s.List} id={listId}>
                {allItems.map((item, index) => (
                    <Item
                        {...item}
                        navId={listId}
                        key={item.id || index}
                        showToggle={false}
                        itemLastActiveId={lastActiveIdToUse}
                        reportsCount={reportsCount}
                        manuallyActiveId={manuallyActiveId}
                        setManuallyActiveId={setManuallyActiveId}
                        isInChildrenList={false}
                    />
                ))}
            </ul>
            {allItems.map((item, index) => {
                const isBookmarkTab = item.id === 'bookmark-tab';
                const hasChildren =
                    item.children && Object.keys(item.children).length > 0;

                const isActiveItem =
                    manuallyActiveId !== null
                        ? manuallyActiveId === item.id
                        : item.isActive;

                if (!isActiveItem) return null;

                return (
                    (hasChildren || isBookmarkTab) && (
                        <ul
                            className={s.ChildrenList}
                            key={`children-${index}`}>
                            {isBookmarkTab ? (
                                <div className={s.BookmarkTab}>
                                    <span className={s.BookmarkTabTitle}>
                                        Favorite pages
                                        <span className={s.BookmarkTabCount}>
                                            ({bookmarkedPages.length})
                                        </span>
                                    </span>
                                    {bookmarkedPages.length === 0 ? (
                                        <div className={s.NoResults}>
                                            <span className={s.NoResultsTitle}>
                                                {t('general.noBookmarks')}
                                            </span>
                                            <span className={s.NoResultsText}>
                                                {t('general.noBookmarksText')}
                                            </span>
                                        </div>
                                    ) : (
                                        <ul className={s.BookmarkTabList}>
                                            {bookmarkedPages.map((bookmark) => (
                                                <li
                                                    className={
                                                        s.BookmarkTabItem
                                                    }
                                                    key={bookmark.url}>
                                                    <a
                                                        className={
                                                            s.BookmarkTabLink
                                                        }
                                                        href={bookmark.url}>
                                                        <span
                                                            className={
                                                                s.BookmarkTabItemText
                                                            }>
                                                            {bookmark.title}
                                                        </span>
                                                        {bookmark?.categories
                                                            ?.length > 0 && (
                                                            <div
                                                                className={classNames(
                                                                    s.CategoryList,
                                                                    {
                                                                        [s.HasMultiple]:
                                                                            bookmark
                                                                                .categories
                                                                                .length >
                                                                            1,
                                                                    }
                                                                )}>
                                                                {bookmark.categories.map(
                                                                    (
                                                                        category,
                                                                        index
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                index
                                                                            }
                                                                            className={
                                                                                s.Category
                                                                            }>
                                                                            {
                                                                                category
                                                                            }
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        )}
                                                    </a>
                                                    <div
                                                        className={
                                                            s.BookmarkTabIcon
                                                        }>
                                                        <Bookmark
                                                            url={bookmark.url}
                                                            isSmall={true}
                                                            alternativeTitle={
                                                                bookmark.title
                                                            }
                                                            categories={
                                                                bookmark.categories
                                                            }
                                                        />
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <li className={s.LastActiveItem}>
                                        <a
                                            className={s.LastActiveLink}
                                            href={item.url}>
                                            <span>{item.title}</span>
                                            <div
                                                className={
                                                    s.LastActiveLinkIcon
                                                }>
                                                <Icon name="Arrow" />
                                            </div>
                                        </a>
                                    </li>
                                    {Object.values(item.children).map(
                                        (child, childIndex) => (
                                            <Item
                                                {...child}
                                                navId={`${navId}-list`}
                                                key={childIndex}
                                                isGrandChild={true}
                                                isParentActive={true}
                                                itemLastActiveId={item.id}
                                                reportsCount={reportsCount}
                                                manuallyActiveId={
                                                    manuallyActiveId
                                                }
                                                setManuallyActiveId={
                                                    setManuallyActiveId
                                                }
                                                isInChildrenList={true}
                                            />
                                        )
                                    )}
                                </>
                            )}
                        </ul>
                    )
                );
            })}
        </div>
    );
};
List.propTypes = {
    items: PropTypes.array.isRequired,
    navId: PropTypes.string.isRequired,
    id: PropTypes.string,
};
const Item = ({
    navId = '',
    isActive = false,
    id = '',
    children = {},
    showToggle = true,
    isGrandChild = false,
    isParentActive = false,
    itemLastActiveId = null,
    reportsCount = 0,
    manuallyActiveId,
    setManuallyActiveId,
    isInChildrenList = false,
    ...item
}) => {
    const isBookmarkTab = id === 'bookmark-tab';
    const hasChildren = Object.keys(children).length > 0 || isBookmarkTab;
    const listId = `${navId}-list`;
    const isManuallyActive = manuallyActiveId === id;
    const isActuallyActive = isActive || isManuallyActive;

    const [isExpanded, setIsExpanded] = useState(isParentActive);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const classes = classNames(
        s.Item,
        {
            [s['Item--Active']]:
                isActuallyActive && (!hasChildren || isInChildrenList),
        },
        { [s['Item--GrandChild']]: isGrandChild },
        { [s['Item--LastActive']]: itemLastActiveId === id }
    );

    return (
        <li className={classes}>
            <div className={s.ItemContent}>
                {hasChildren && !isInChildrenList ? (
                    <button
                        className={s.ItemButton}
                        onClick={() => setManuallyActiveId(id)}
                        aria-expanded={isActuallyActive}
                        aria-controls={`${listId}-children`}>
                        <span>{item.title}</span>
                        {item.icon && (
                            <div className={s.IconWrapper}>
                                <Icon
                                    name={`${item.icon}Mobile`}
                                    className={s.Icon}
                                />
                            </div>
                        )}
                    </button>
                ) : (
                    <>
                        <CustomLink {...item} reportsCount={reportsCount} />
                        {showToggle && hasChildren && (
                            <button
                                className={s.Toggle}
                                type="button"
                                onClick={toggleExpanded}
                                aria-expanded={isExpanded}
                                aria-controls={`${listId}-children`}></button>
                        )}
                    </>
                )}
            </div>

            {isInChildrenList && hasChildren && (
                <AnimateUpDown isVisible={isExpanded}>
                    <ul className={s.GrandChildList} id={`${listId}-children`}>
                        {Object.values(children).map((child, index) => (
                            <Item
                                {...child}
                                navId={listId}
                                key={index}
                                isGrandChild={true}
                                isParentActive={isExpanded}
                                itemLastActiveId={itemLastActiveId}
                                reportsCount={reportsCount}
                                manuallyActiveId={manuallyActiveId}
                                setManuallyActiveId={setManuallyActiveId}
                                isInChildrenList={true}
                            />
                        ))}
                    </ul>
                </AnimateUpDown>
            )}
        </li>
    );
};

Item.propTypes = {
    navId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    children: PropTypes.object,
    isActive: PropTypes.bool,
};

const CustomLink = ({
    title = '',
    url = '',
    target = null,
    rel = null,
    attrTitle = null,
    className = null,
    linkTextClassName = null,
    icon = '',
    isReportPage = false,
    reportsCount = 0,
}) => {
    const classes = classNames(s.Link, className);

    const linkTextClasses = classNames(s.LinkText, linkTextClassName);

    return (
        <a
            className={classes}
            href={url}
            target={target}
            rel={rel}
            title={attrTitle}>
            {icon && (
                <div className={s.IconWrapper}>
                    {isReportPage && reportsCount > 0 && (
                        <div className={s.LinkNotification}>{reportsCount}</div>
                    )}
                    {icon && <Icon name={`${icon}Mobile`} className={s.Icon} />}
                </div>
            )}

            {typeof title === 'string' ? (
                <span
                    className={linkTextClasses}
                    dangerouslySetInnerHTML={{ __html: title }}
                />
            ) : (
                <span className={linkTextClasses}>{title}</span>
            )}
        </a>
    );
};

CustomLink.propTypes = {
    title: PropTypes.node.isRequired,
    url: PropTypes.string.isRequired,
    target: PropTypes.string,
    rel: PropTypes.string,
    attrTitle: PropTypes.string,
    className: PropTypes.string,
    linkTextClassName: PropTypes.string,
    linkIconClassName: PropTypes.string,
};

export default MobileMenu;
